import { type FC, useCallback, useEffect, useMemo, useState } from 'react'

import { WelcomeMessageApiV3RequestDto, WelcomeMessageScope } from '@api'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'
import { unstable_usePrompt } from 'react-router-dom'

import ClockIcon from '@/assets/icons/icon-clock.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { toast } from '@/models/toasts.ts'
import {
  displayTime,
  getHoursAndMinutes,
} from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import { resolver } from '@/modules/creator-settings/welcome-messages/components/validation.ts'
import {
  $bookedIntervals,
  $excludeList,
  $followUpEnabled,
  $fromTime,
  $includeList,
  $isFormDirty,
  $messages,
  $toTime,
  $welcomeMessage,
  $welcomeMessageSortOptions,
  $withoutTimeframe,
  addMessage,
  removeMessage,
  setExcludeList,
  setFollowUpEnabled,
  setIncludeList,
  setTimeframe,
  setWelcomeMessage,
  toggleWithoutTimeframe,
  updateMessage,
} from '@/modules/creator-settings/welcome-messages/models.ts'
import { useWelcomeMessageSettings } from '@/modules/creator-settings/welcome-messages/query/use-welcome-message-settings.tsx'
import {
  useCreateWelcomeMessage,
  useUpdateWelcomeMessage,
} from '@/modules/creator-settings/welcome-messages/query/welcome-message-query.ts'
import { FollowUpsMessages } from '@/modules/message/components/follow-ups/follow-ups-messages.tsx'
import { MessageSequenceEditor } from '@/modules/message/components/sequence/message-sequence-editor'
import { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import {
  $followUpMessages,
  $sendAfterMinutes,
  $sendIfNotSeenParentMessage,
  resetFollowUpState,
  resetSendAfterMinutesError,
  validateSendAfterMinutes,
} from '@/modules/message/models.ts'
import { EmptyMessages } from '@/modules/online-mass-messages/ui/error/empty-messages'
import { AppRoutes } from '@/utils/constants.ts'

import { TrackingLinksSelect } from './tracking-links-select'

const dialogTitle = 'Add a Welcome Message'
const dialogDescription = (
  <>
    Use the button to insert the fan&apos;s name, which will replace the label
    with their actual name. If the fan doesn&apos;t have a name, the label will
    be replaced with the fallback name.
  </>
)

const editorComponents = [
  'text',
  'media',
  'price',
  'fallback',
  'customAndFanNameLabels',
] as const

interface Props {}

export const CreateWelcomeMessagesPage: FC<Props> = () => {
  const redirect = useNavigate()
  const creatorId = useUnit($creatorId)
  const [isLoading, setIsLoading] = useState(false)
  const sortOption = useUnit($welcomeMessageSortOptions)
  const followUpMessages = useUnit($followUpMessages)
  const sendAfterMinutes = useUnit($sendAfterMinutes)
  const sendIfNotSeenParentMessage = useUnit($sendIfNotSeenParentMessage)
  const { messages: welcomeMessages } = useWelcomeMessageSettings()
  const messages = useUnit($messages)
  const excludeList = useUnit($excludeList)
  const includeList = useUnit($includeList)
  const fromTime = useUnit($fromTime)
  const toTime = useUnit($toTime)
  const isFormDirty = useUnit($isFormDirty)
  const followUpEnabled = useUnit($followUpEnabled)
  const withoutTimeframe = useUnit($withoutTimeframe)
  const data = useUnit($welcomeMessage)
  const bookedIntervals = useUnit($bookedIntervals)

  useEffect(() => {
    return () => {
      resetFollowUpState()
    }
  }, [])

  const updateWelcomeMessage = useUpdateWelcomeMessage(
    WelcomeMessageScope.NEW_FANS,
    sortOption,
  )
  const createWelcomeMessage = useCreateWelcomeMessage(
    WelcomeMessageScope.NEW_FANS,
    sortOption,
  )

  const openTimeSelectDialog = () => {
    openDialog({
      type: DIALOG_TYPE.SELECT_TIME_POPUP,
      title: 'Timeframe',
      fromTime,
      toTime,
      id: data?.id,
      update: setTimeframe,
      bookedIntervals,
    })
  }

  unstable_usePrompt({
    when: isFormDirty,
    message: 'You have unsaved changes. Are you sure you want to leave?',
  })

  const validateForm = () => {
    validateSendAfterMinutes()
    toast.error('Please fill required fields')
  }

  const createMessage = async () => {
    if (followUpEnabled && !sendAfterMinutes) {
      validateForm()

      return
    }
    setIsLoading(true)

    const payload = {
      messages,
      timeframeStart: withoutTimeframe ? null : getHoursAndMinutes(fromTime!),
      timeframeEnd: withoutTimeframe ? null : getHoursAndMinutes(toTime!),
      includedTrackedLinks: includeList.map((id) => Number(id)),
      excludedTrackedLinks: excludeList.map((id) => Number(id)),
      followUpEnabled,
      followUpDto: {
        messages: followUpMessages,
        sendAfterMinutes,
        sendIfNotSeenParentMessage,
      },
    } as WelcomeMessageApiV3RequestDto

    try {
      if (data) {
        if (!data.id) {
          throw new Error('Message ID is not defined')
        }
        const response = await updateWelcomeMessage.mutateAsync({
          id: data.id,
          message: payload,
        })

        // @ts-ignore
        setWelcomeMessage(response)
      } else {
        const response = await createWelcomeMessage.mutateAsync(payload)

        // @ts-ignore
        setWelcomeMessage(response)
      }

      setTimeout(() => {
        redirect(AppRoutes.Creator.Settings.WelcomeMessages.asUrl(creatorId!))
      }, 0)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFollowUpEnable = useCallback(() => {
    resetSendAfterMinutesError()
    setFollowUpEnabled(!followUpEnabled)
  }, [followUpEnabled])

  const handleCancel = useCallback(() => {
    redirect(AppRoutes.Creator.Settings.WelcomeMessages.asUrl(creatorId!))
  }, [creatorId, redirect])

  const isDefaultMessageExist = useMemo(() => {
    const defaultMessage = welcomeMessages.find(
      (m) => !m.timeframeStart && !m.timeframeEnd,
    )

    if (!defaultMessage) {
      return false
    }

    return defaultMessage.id !== data?.id
  }, [data?.id, welcomeMessages])

  const disableSave =
    !messages.length ||
    (followUpEnabled && !followUpMessages.length) ||
    (!!data && !isFormDirty)

  return (
    <Page
      title={(data ? 'Edit' : 'Create') + ' Welcome Messages'}
      description="Configure your Welcome Message to be sent out to new fans who subscribe during the selected time frame."
      backToPage={{
        name: 'Welcome Messages',
        url: AppRoutes.Creator.Settings.WelcomeMessages.toString(),
      }}
      pageClassName="relative pb-0"
    >
      <div>
        <Separator />
        <div className="text-title flex items-center gap-2 py-8 font-medium">
          <Checkbox
            checked={withoutTimeframe}
            onClick={toggleWithoutTimeframe}
            disabled={isDefaultMessageExist}
          />
          Create a message without timeframe{' '}
          <Tooltip
            side="right"
            sideOffset={20}
            content={
              isDefaultMessageExist
                ? 'Default message already exist'
                : 'If message for this time not exist this message will be as default'
            }
          >
            <InfoIcon className="text-secondary-300 h-4 w-4" />
          </Tooltip>
        </div>
        <Separator />

        {!withoutTimeframe && (
          <>
            <SettingsOption
              title="Timeframe"
              description="The timeframe in which this message should be sent out to a fan."
              mobileVertical
              biggerTitle
            >
              <div
                className="border-secondary-300 bg-card text-secondary-300 hover:border-secondary-100 flex cursor-pointer select-none items-center gap-2 rounded-md border-2 px-3 py-2"
                onClick={openTimeSelectDialog}
              >
                <ClockIcon />
                <span>{displayTime(fromTime!)}</span>
                {'- '}
                {displayTime(toTime!)}
              </div>
            </SettingsOption>
            <Separator />
          </>
        )}

        <div className="flex flex-wrap justify-between">
          <SettingsOption
            title="Fan Links Control"
            description="Include or exclude fans from specific tracking links for this Welcome Message"
            mobileVertical
            biggerTitle
          />
          <div className="flex flex-col gap-10 pb-6 pt-0 md:flex-row">
            <TrackingLinksSelect
              label="Only include selected Tracking Link"
              value={includeList}
              onChange={setIncludeList}
              excludeValues={excludeList}
            />
            <TrackingLinksSelect
              label="Exclude the selected Tracking Link"
              value={excludeList}
              onChange={setExcludeList}
              excludeValues={includeList}
            />
          </div>
        </div>

        <Separator />

        <EmptyProvider render={() => <EmptyMessages action="Add Message" />}>
          <MessageSequenceEditor
            title="Messages"
            description="Configure multiple messages that will be sent to your fans"
            placeholder="Hey %name%, how are you?"
            addMessageLabel="Add Message"
            dialogTitle={dialogTitle}
            dialogDescription={dialogDescription}
            components={editorComponents}
            schema={resolver}
            entries={messages}
            onCreate={addMessage}
            onUpdate={updateMessage}
            onRemove={removeMessage}
            type={MessageContextType.FollowUpMessage}
          />
        </EmptyProvider>

        <div className="pt-[32px]">
          <FollowUpsMessages
            type="Welcome Message"
            dialogTitle="Add Follow Up Welcome Message"
            enabled={followUpEnabled}
            toggleChange={handleFollowUpEnable}
            data={data?.followUpDto}
          />
        </div>
      </div>
      <div className="bg-background sticky bottom-0 z-[48] mt-8 flex justify-end gap-4 border-t px-0 py-4 lg:-mx-16 lg:px-4 lg:pr-16">
        <Button
          onClick={handleCancel}
          variant="outline"
          type="button"
        >
          Cancel
        </Button>
        <Button
          disabled={disableSave}
          onClick={createMessage}
          isLoading={isLoading}
        >
          {data ? 'Update' : 'Create'} Welcome Message
        </Button>
      </div>
    </Page>
  )
}
